/**
 * Locate lifetime route progress on a repeatable route. Exact completions stay
 * at the finish of the loop just completed. A completed enrollment stays at
 * the endpoint even if its final step batch overshot the target.
 */
export function challengeRouteLoopProgress(route) {
    return challengeRouteLoopProgressFromFraction(route.progress / Math.max(1, route.target), route.status === "completed");
}
/** Locate an unbounded progress fraction on its current route loop. */
export function challengeRouteLoopProgressFromFraction(totalFraction, finished = false) {
    const safeFraction = Math.max(0, finished ? Math.min(1, totalFraction) : totalFraction);
    if (safeFraction === 0)
        return { loop: 1, fraction: 0, percent: 0 };
    const completedLoops = Math.floor(safeFraction);
    const remainder = safeFraction - completedLoops;
    const onFinishLine = remainder < 1e-9;
    const loop = onFinishLine ? Math.max(1, completedLoops) : completedLoops + 1;
    const fraction = onFinishLine ? 1 : remainder;
    return { loop, fraction, percent: Math.round(fraction * 100) };
}
/** Compact progress for rosters and map popovers. */
export function challengeRouteLoopLabel(totalFraction) {
    const progress = challengeRouteLoopProgressFromFraction(totalFraction);
    return progress.loop > 1
        ? `Loop ${progress.loop} · ${progress.percent}%`
        : `${progress.percent}%`;
}
/**
 * Product facts for a curated Challenge route. Both web and native renderers
 * consume these facts instead of each rediscovering progress and milestone rules.
 */
export function challengeRouteProgress(route) {
    return challengeRouteLoopProgress(route).fraction;
}
export function challengeRoutePercent(route) {
    return Math.round(challengeRouteProgress(route) * 100);
}
export function nextChallengeMilestone(route) {
    const progress = challengeRouteProgress(route);
    return route.routeMilestones.find((milestone) => milestone.fraction > progress) ?? null;
}
/** How far the next waypoint is, in the two units that mean something: */
export function challengeMilestoneGap(route) {
    const milestone = nextChallengeMilestone(route);
    if (!milestone)
        return null;
    const remaining = Math.max(0, milestone.fraction - challengeRouteProgress(route));
    return {
        title: milestone.title,
        steps: Math.max(0, Math.round(remaining * route.target)),
        meters: route.distanceMeters === null ? null : Math.round(remaining * route.distanceMeters),
    };
}
/** Represents a chosen route using the same catalogue shape as an unchosen route. */
export function challengeRouteFromEnrollment(route) {
    return {
        instanceId: route.instanceId,
        name: route.name,
        description: "A route that gives ordinary movement a visible destination.",
        target: route.target,
        unit: route.unit,
        artwork: null,
        introduction: route.introduction,
        imageUrl: route.imageUrl,
        imageAlt: route.imageAlt,
        imageAttribution: route.imageAttribution,
        routePath: route.routePath,
        routeMilestones: route.routeMilestones,
        routeMode: route.routeMode,
        mapCenter: route.mapCenter,
        mapZoom: route.mapZoom,
        distanceMeters: route.distanceMeters,
        community: { memberCount: 1, members: [], completedCount: 0, completedMembers: [] },
    };
}
/** Prefer the catalog route so an enrolled preview retains live community presence. */
export function challengeRouteForEnrollment(enrollment, catalogRoute) {
    return catalogRoute?.instanceId === enrollment.instanceId
        ? catalogRoute
        : challengeRouteFromEnrollment(enrollment);
}
/** Supplies the map adapter with a zero-progress preview for an unchosen route. */
export function challengeRoutePreview(route) {
    return {
        name: route.name,
        target: route.target,
        progress: 0,
        routePath: route.routePath,
        routeMilestones: route.routeMilestones,
    };
}
//# sourceMappingURL=challenge-presentation.js.map